import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Container, Grid, Typography, styled } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ContactModal from "./contactModel";

interface LetsTalkProps {
  onLetsTalk?: () => void;
}

const Section = styled(Box)(({ theme }) => ({
  backgroundColor: "#0d1620",
  color: "#fff",
  paddingTop: theme.spacing(11),
  paddingBottom: theme.spacing(11),
  [theme.breakpoints.down("md")]: {
    paddingTop: theme.spacing(9),
    paddingBottom: theme.spacing(9),
  },
}));

const Label = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#7ec1e2",
  marginBottom: 18,
});

const Title = styled(Typography)(({ theme }) => ({
  fontSize: "2.4rem",
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: "-0.02em",
  color: "#fff",
  marginBottom: theme.spacing(2),
  maxWidth: 540,
  [theme.breakpoints.down("md")]: { fontSize: "1.95rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1.65rem" },
}));

const Lead = styled(Typography)(({ theme }) => ({
  fontSize: "1.02rem",
  lineHeight: 1.65,
  color: "rgba(255, 255, 255, 0.7)",
  marginBottom: theme.spacing(4),
  maxWidth: 480,
}));

const PrimaryButton = styled(Button)({
  padding: "12px 24px",
  borderRadius: 6,
  fontWeight: 600,
  fontSize: "0.95rem",
  textTransform: "none",
  backgroundColor: "#2f81ad",
  color: "#fff",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#1d648c", boxShadow: "none" },
  transition: "background-color 0.2s ease",
});

const EmailLink = styled("a")({
  marginLeft: 18,
  fontSize: "0.95rem",
  fontWeight: 500,
  color: "rgba(255, 255, 255, 0.7)",
  textDecoration: "none",
  transition: "color 0.2s ease",
  "&:hover": { color: "#fff" },
});

const InfoBlock = styled(Box)({
  padding: 26,
  borderRadius: 10,
  backgroundColor: "rgba(255, 255, 255, 0.04)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
});

const InfoRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "14px 0",
  borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
  "&:last-of-type": { borderBottom: "none" },
});

const InfoLabel = styled(Typography)({
  fontSize: "0.85rem",
  color: "rgba(255, 255, 255, 0.55)",
});

const InfoValue = styled(Typography)({
  fontSize: "0.92rem",
  fontWeight: 600,
  color: "#fff",
});

const LetsTalk: React.FC<LetsTalkProps> = ({ onLetsTalk }) => {
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.1 }
    );
    obs.observe(node);
    return () => obs.unobserve(node);
  }, []);

  const handleClick = () => {
    setModalOpen(true);
    onLetsTalk?.();
  };

  return (
    <>
      <Section ref={ref} component="section" aria-labelledby="letstalk-heading">
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(12px)",
                  transition: "opacity 0.5s ease, transform 0.5s ease",
                }}
              >
                <Label>Let's talk</Label>
                <Title id="letstalk-heading" variant="h2">
                  Have a project in mind?
                </Title>
                <Lead>
                  Tell us what you're building. We'll come back within 48 hours
                  with scope, timeline, and a fixed-price or T&amp;M estimate —
                  no obligation.
                </Lead>
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                  <PrimaryButton
                    onClick={handleClick}
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                    disableElevation
                  >
                    Book a discovery call
                  </PrimaryButton>
                  <EmailLink href="mailto:hello@smartittec.com">
                    hello@smartittec.com
                  </EmailLink>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(16px)",
                  transition: "opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s",
                }}
              >
                <InfoBlock>
                  <InfoRow>
                    <InfoLabel>Response time</InfoLabel>
                    <InfoValue>Under 48 hours</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Minimum engagement</InfoLabel>
                    <InfoValue>$5,000 USD</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Availability</InfoLabel>
                    <InfoValue>Now booking Q3 2026</InfoValue>
                  </InfoRow>
                </InfoBlock>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Section>

      <ContactModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};

export default LetsTalk;
